/**
 * ClaimGroupsDisplay Component
 *
 * Displays grouped claims with filtering and explanation capabilities.
 *
 * Features:
 * - Summary cards showing counts by group type
 * - Filterable claim groups
 * - Model badges showing which models contributed
 * - Expandable claim variations
 * - On-demand explanations
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { logger } from '../../../lib/logging';
import { ModelBadge } from '../../../components/results/shared/ModelBadge';
import { getModelLogo } from '../../../utils/logoMapping';
import { useDevice } from '../hooks/useDevice';
import { ClaimCard } from '../../../components/consensus/ClaimCard';
import { SourcePoolDisplay } from '../../../components/consensus/SourcePoolDisplay';
import { deduplicateAndCategorizeCitations } from '../../../utils/citationUtils';
import { ConsensusGuideModal } from '../../../components/results/consensus/ConsensusGuideModal';
import type { ModelResponse } from '../../../services/comparison';

interface ClaimCitation {
  text: string;
  type: 'url' | 'reference' | 'doi';
  normalizedDomain?: string;
  position?: number;
  modelKey?: string;
}

interface ClaimMember {
  modelKey: string;
  claimText: string;
  claimType: 'fact' | 'opinion' | 'recommendation' | 'warning';
  criterionRelevance: string[];
  similarityToRepresentative: number;
}

interface ClaimGroup {
  groupId: string;
  groupType: 'universal' | 'majority' | 'disputed' | 'unique';
  representativeClaimText: string;
  avgSimilarityScore: number;
  memberCount: number;
  modelsRepresented: string[];
  members: ClaimMember[];
  explanation: string | null;
  hasExplanation: boolean;
}

interface ClaimGroupsData {
  comparisonId: string;
  summary: {
    totalGroups: number;
    universal: number;
    majority: number;
    disputed: number;
    unique: number;
  };
  groups: ClaimGroup[];
}

interface ClaimGroupsDisplayProps {
  comparisonId: string;
  responses: ModelResponse[];
}

const GROUP_TYPE_CONFIG = {
  universal: {
    label: 'Universal Agreement',
    icon: '🟢',
    color: 'green',
    description: 'All models agree'
  },
  majority: {
    label: 'Majority Agreement',
    icon: '🔵',
    color: 'blue',
    description: 'Most models agree'
  },
  disputed: {
    label: 'Disputed',
    icon: '🟡',
    color: 'yellow',
    description: 'Some models differ'
  },
  unique: {
    label: 'Unique Insight',
    icon: '⚪',
    color: 'gray',
    description: 'Only one model'
  }
};

/**
 * Parse modelKey format: "provider:model" => { provider, model }
 * Example: "openai:gpt-4o-mini" => { provider: "openai", model: "gpt-4o-mini" }
 */
const parseModelKey = (modelKey: string): { provider: string; model: string } => {
  const [provider, ...modelParts] = modelKey.split(':');
  return {
    provider: provider || 'unknown',
    model: modelParts.join(':') || modelKey
  };
};

/**
 * Convert text with URLs to JSX with clickable links
 * Detects URLs and wraps them in anchor tags
 */
const linkifyText = (text: string): React.ReactNode => {
  // URL regex pattern
  const urlPattern = /(https?:\/\/[^\s]+)/g;

  const parts = text.split(urlPattern);

  return parts.map((part, index) => {
    if (part.match(urlPattern)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-cyan-400 hover:text-cyan-300 underline"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

export const ClaimGroupsDisplay: React.FC<ClaimGroupsDisplayProps> = ({ comparisonId, responses }) => {
  // 🔍 DIAGNOSTIC: Verify this component is rendering and modelKey propagation
  console.log('[🔍 DIAGNOSTIC] ClaimGroupsDisplay MOUNTED', {
    comparisonId,
    responsesReceived: !!responses,
    responsesCount: responses?.length || 0,
    responsesPreview: responses?.slice(0, 2).map(r => ({
      id: r.id,
      provider: r.provider?.name || r.providerId,
      model: r.model?.name || r.modelId,
      modelKey: (r as any).modelKey || 'NO_MODELKEY_FIELD',
      hasModelKeyField: !!(r as any).modelKey
    })),
    timestamp: new Date().toISOString()
  });

  const navigate = useNavigate();
  const [data, setData] = useState<ClaimGroupsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [visibleExplanations, setVisibleExplanations] = useState<Set<string>>(new Set());
  const [loadingExplanation, setLoadingExplanation] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [originalSummary, setOriginalSummary] = useState<any>(null);
  const [citationsByResponse, setCitationsByResponse] = useState<Map<string, ClaimCitation[]>>(new Map());
  const [extractingCitations, setExtractingCitations] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState({ current: 0, total: 0 });
  const [showMissingCitationsWarning, setShowMissingCitationsWarning] = useState(false);

  // Mobile-specific state
  const { isMobile } = useDevice();
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  // Consensus Guide Modal state
  const [showGuideModal, setShowGuideModal] = useState(false);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setPortalTarget(document.body);
    }
  }, []);

  useEffect(() => {
    if (!filterSheetOpen || !portalTarget) return;

    const previousOverflow = portalTarget.style.overflow;
    portalTarget.style.overflow = 'hidden';

    return () => {
      portalTarget.style.overflow = previousOverflow;
    };
  }, [filterSheetOpen, portalTarget]);

  /**
   * Get proper display name and provider for a model from responses
   * Ensures consistency with Comparison List View naming
   */
  const getModelDisplayInfo = (modelKey: string): { provider: string; displayName: string } => {
    const { provider, model } = parseModelKey(modelKey);

    // Find matching response using the standardized modelKey field
    // The modelKey is now added to responses by ComparisonDetail via buildModelKey utility
    const response = responses.find((r: ModelResponse) => {
      // Use the modelKey field if available (new standardized approach)
      if ((r as any).modelKey) {
        return (r as any).modelKey === modelKey;
      }
      // Fallback to manual construction for backward compatibility
      const responseModelKey = `${r.provider?.name || r.providerName || r.providerId}:${r.model?.name || r.modelName || r.modelId}`;
      return responseModelKey === modelKey;
    });

    if (!response) {
      logger.warn('[ClaimGroups] No matching response found for modelKey', {
        modelKey,
        availableModelKeys: responses.map(r => (r as any).modelKey || 'no-modelKey-field')
      });
    }

    return {
      provider: response?.provider?.name || response?.providerName || provider,
      displayName: response?.model?.displayName || response?.modelName || model
    };
  };

  const renderModelOptionContent = (
    provider: string,
    modelId: string,
    displayName: string
  ) => {
    const logoSrc = getModelLogo(modelId, provider);
    const fallbackInitials = displayName.substring(0, 2).toUpperCase();

    return (
      <div className="flex items-center gap-3 w-full text-left">
        <div className="w-10 h-10 rounded-lg bg-slate-900/50 border border-slate-700/70 flex items-center justify-center text-slate-200 flex-shrink-0">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={`${displayName} logo`}
              className="w-8 h-8 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
              <span className="text-sm font-semibold">{fallbackInitials}</span>
            )}
        </div>
        <div className="flex-1 min-w-0 text-sm font-medium text-slate-100 truncate">{displayName}</div>
      </div>
    );
  };

  const ITEMS_PER_PAGE = 3;

  // Log component mount with data status
  useEffect(() => {
    logger.info('[ClaimGroups] Component mounted', {
      comparisonId,
      responsesCount: responses?.length || 0,
      hasResponses: !!responses && responses.length > 0,
      responseModelKeys: responses?.map(r =>
        `${r.provider?.name || r.providerName || r.providerId}:${r.model?.name || r.modelName || r.modelId}`
      ) || []
    });
  }, [comparisonId, responses]);

  // Effect 1: Fetch claim groups when comparison or filter changes
  useEffect(() => {
    console.log('[🔍 DIAGNOSTIC] useEffect 1 FIRED - fetchClaimGroups', {
      comparisonId,
      filter,
      timestamp: new Date().toISOString()
    });
    fetchClaimGroups();
  }, [comparisonId, filter]);

  // Effect 2: Fetch citations when responses are available
  // Note: Citation extraction now happens automatically during Consensus Analysis
  // We just need to fetch the already-extracted data from the database
  useEffect(() => {
    console.log('[🔍 DIAGNOSTIC] useEffect 2 FIRED - fetch citations', {
      comparisonId,
      responsesAvailable: !!responses,
      responsesCount: responses?.length || 0,
      willSkip: !responses || responses.length === 0,
      timestamp: new Date().toISOString()
    });

    if (!responses || responses.length === 0) {
      logger.warn('[Citations] No responses available yet, skipping fetch');
      return;
    }

    const fetchCitations = async () => {
      try {
        logger.info('[Citations] Fetching citations from database', {
          comparisonId,
          responseCount: responses.length,
          responseIds: responses.map(r => r.id)
        });

        await fetchCitationsForResponses();

        logger.info('[Citations] Fetch complete', { comparisonId });
      } catch (error) {
        logger.error('[Citations] Fetch failed', { error });
      }
    };
    fetchCitations();
  }, [comparisonId, responses]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [filter, selectedModel]);

  /**
   * Manually trigger citation re-extraction for all responses
   * This is a backup/recovery function - normally citations are extracted during Consensus Analysis
   * Only used when the user clicks "Extract Citations Now" if data is missing
   */
  const triggerCitationExtraction = async () => {
    console.log('[Citations] Manual re-extraction triggered by user', {
      comparisonId,
      responsesCount: responses.length,
      responseIds: responses.map(r => r.id),
      timestamp: new Date().toISOString()
    });

    try {
      logger.info('[Citations] Starting manual extraction for all responses', {
        comparisonId,
        responseCount: responses.length
      });

      // Force re-extraction for all responses (no caching check)
      const extractionPromises = responses.map(async (response, index) => {
        try {
          logger.info('[Citations] Force extracting', { responseId: response.id, index: index + 1 });

          const extractRes = await fetch(`/api/claim-extraction/${response.id}/extract`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });

          if (!extractRes.ok) {
            const errorText = await extractRes.text();
            logger.error('[Citations] Extraction failed', {
              responseId: response.id,
              status: extractRes.status,
              error: errorText
            });
            setExtractionProgress(prev => ({ ...prev, current: prev.current + 1 }));
            return { responseId: response.id, success: false, error: errorText };
          }

          const extractData = await extractRes.json();
          logger.info('[Citations] Extraction complete', {
            responseId: response.id,
            claims: extractData.claims?.length || 0,
            totalCitations: extractData.totalCitations || 0
          });

          setExtractionProgress(prev => ({ ...prev, current: prev.current + 1 }));
          return { responseId: response.id, success: true, data: extractData };
        } catch (error) {
          logger.error('[Citations] Extraction error', {
            responseId: response.id,
            error: error instanceof Error ? error.message : String(error)
          });
          setExtractionProgress(prev => ({ ...prev, current: prev.current + 1 }));
          return { responseId: response.id, success: false, error };
        }
      });

      const results = await Promise.all(extractionPromises);
      const successCount = results.filter(r => r.success).length;
      const totalCitations = results.reduce((sum, r) => sum + (r.data?.totalCitations || 0), 0);

      logger.info('[Citations] Manual extraction batch complete', {
        total: responses.length,
        successful: successCount,
        failed: responses.length - successCount,
        totalCitations
      });

    } catch (error) {
      logger.error('[Citations] Manual extraction failed', {
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  };

  const fetchClaimGroups = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = filter
        ? `/api/consensus/${comparisonId}/claim-groups?groupType=${filter}`
        : `/api/consensus/${comparisonId}/claim-groups`;

      logger.info('Fetching claim groups', { comparisonId, filter });

      const res = await fetch(url);
      if (!res.ok) {
        throw new Error('Failed to fetch claim groups');
      }

      const result = await res.json();
      setData(result);

      // Store original summary on first load (no filter)
      if (!filter && !originalSummary) {
        setOriginalSummary(result.summary);
      }

      logger.info('Claim groups loaded', {
        comparisonId,
        totalGroups: result.summary.totalGroups
      });
    } catch (err) {
      logger.error('[ClaimGroups] Failed to fetch claim groups', {
        error: err instanceof Error ? err.message : String(err),
        comparisonId,
        filter,
        timestamp: new Date().toISOString()
      });
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCitationsForResponses = async () => {
    console.log('[🔍 DIAGNOSTIC] fetchCitationsForResponses CALLED', {
      comparisonId,
      responsesCount: responses.length,
      responseIds: responses.map(r => r.id),
      timestamp: new Date().toISOString()
    });

    try {
      logger.info('Fetching citations for all responses', {
        comparisonId,
        responseCount: responses.length
      });

      // Fetch citations for all responses in parallel
      const citationsPromises = responses.map(async (response) => {
        try {
          console.log('[🔍 DIAGNOSTIC] Fetching citations API', {
            url: `/api/claim-extraction/${response.id}/claims`,
            responseId: response.id,
            timestamp: new Date().toISOString()
          });

          const res = await fetch(`/api/claim-extraction/${response.id}/claims`);

          console.log('[🔍 DIAGNOSTIC] Citations API response', {
            responseId: response.id,
            status: res.status,
            ok: res.ok,
            timestamp: new Date().toISOString()
          });

          if (!res.ok) {
            logger.warn('Failed to fetch citations for response', { responseId: response.id });
            return { responseId: response.id, citations: [] };
          }
          const data = await res.json();

          // Extract all citations from all claims with modelKey annotation
          // Use the standardized modelKey field if available
          const modelKey = (response as any).modelKey || `${response.provider?.name || response.providerName || response.providerId}:${response.model?.name || response.modelName || response.modelId}`;
          const allCitations: ClaimCitation[] = data.claims?.flatMap((claim: any) =>
            (claim.citations || []).map((cit: any) => ({
              ...cit,
              modelKey
            }))
          ) || [];

          return { responseId: response.id, citations: allCitations };
        } catch (error) {
          logger.error('Error fetching citations for response', {
            responseId: response.id,
            error
          });
          return { responseId: response.id, citations: [] };
        }
      });

      const results = await Promise.all(citationsPromises);

      // Build citation map
      const citationsMap = new Map<string, ClaimCitation[]>();
      results.forEach(({ responseId, citations }) => {
        citationsMap.set(responseId, citations);
      });

      setCitationsByResponse(citationsMap);

      logger.info('[Citations] Citations loaded and stored in Map', {
        responsesWithCitations: results.filter(r => r.citations.length > 0).length,
        totalCitations: results.reduce((sum, r) => sum + r.citations.length, 0),
        mapSize: citationsMap.size,
        mapKeys: Array.from(citationsMap.keys()),
        perResponse: results.map(r => ({
          responseId: r.responseId,
          citationCount: r.citations.length,
          sampleCitation: r.citations[0] ? {
            text: r.citations[0].text,
            type: r.citations[0].type,
            modelKey: r.citations[0].modelKey
          } : null
        }))
      });

      // Check if citations are missing but responses likely contain URLs
      checkForMissingCitations(citationsMap);
    } catch (error) {
      logger.error('Failed to fetch citations', { error });
    }
  };

  /**
   * Detect if responses contain URLs but citations weren't extracted
   * Shows a warning banner with option to re-extract
   */
  const checkForMissingCitations = (citationsMap: Map<string, ClaimCitation[]>) => {
    // Count total citations
    const totalCitations = Array.from(citationsMap.values()).reduce(
      (sum, citations) => sum + citations.length,
      0
    );

    // If we have citations, no need to show warning
    if (totalCitations > 0) {
      setShowMissingCitationsWarning(false);
      return;
    }

    // Check if any responses contain URLs (potential citations)
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    const hasUrlsInResponses = responses.some(response => {
      // Check if response content contains URLs
      const content = response.content || response.text || '';
      return urlPattern.test(content);
    });

    if (hasUrlsInResponses) {
      logger.warn('[Citations] Responses contain URLs but no citations extracted', {
        comparisonId,
        responsesCount: responses.length,
        citationsCount: totalCitations
      });
      setShowMissingCitationsWarning(true);
    } else {
      setShowMissingCitationsWarning(false);
    }
  };

  /**
   * Manually trigger citation re-extraction
   * Useful when extraction failed or was never triggered
   */
  const handleRetriggerCitationExtraction = async () => {
    logger.info('[Citations] Manual re-extraction triggered', { comparisonId });
    setShowMissingCitationsWarning(false); // Hide warning during extraction

    try {
      // Keep extractingCitations=true for the entire process
      setExtractingCitations(true);
      setExtractionProgress({ current: 0, total: responses.length });

      await triggerCitationExtraction();
      await fetchCitationsForResponses();

      logger.info('[Citations] Manual re-extraction complete', { comparisonId });
    } catch (error) {
      logger.error('[Citations] Manual re-extraction failed', { error });
    } finally {
      setExtractingCitations(false);
    }
  };

  const requestExplanation = async (groupId: string) => {
    try {
      setLoadingExplanation(groupId);

      logger.info('Requesting explanation', { comparisonId, groupId });

      const res = await fetch(`/api/consensus/${comparisonId}/explain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groupId })
      });

      if (!res.ok) {
        throw new Error('Failed to generate explanation');
      }

      const result = await res.json();

      // Update the group with the explanation
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          groups: prev.groups.map(g =>
            g.groupId === groupId
              ? { ...g, explanation: result.explanation, hasExplanation: true }
              : g
          )
        };
      });

      // Automatically show the explanation after loading
      setVisibleExplanations(prev => {
        const next = new Set(prev);
        next.add(groupId);
        return next;
      });

      logger.info('Explanation loaded', {
        groupId,
        cached: result.cached
      });
    } catch (err) {
      logger.error('Failed to load explanation', { error: err });
    } finally {
      setLoadingExplanation(null);
    }
  };

  const toggleExplanation = (groupId: string) => {
    setVisibleExplanations(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const toggleExpanded = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  /**
   * Get citations for a claim group by aggregating citations from all member responses
   */
  const getCitationsForGroup = (group: ClaimGroup): { groupCitations: ClaimCitation[]; allCitations: ClaimCitation[] } => {
    console.log('[🔍 DIAGNOSTIC] getCitationsForGroup CALLED', {
      groupId: group.id,
      memberCount: group.members.length,
      citationsMapSize: citationsByResponse.size,
      citationsMapKeys: Array.from(citationsByResponse.keys()),
      timestamp: new Date().toISOString()
    });

    const allCitations: ClaimCitation[] = [];

    // Collect ALL citations across all responses for shared citation detection
    citationsByResponse.forEach(citations => {
      allCitations.push(...citations);
    });

    // Filter citations to only those from models that contributed to this claim group
    // This ensures Model-Specific Sources only shows citations from models referenced in the claim card
    // FALLBACK: If modelsRepresented is empty (claim_group_members table not populated),
    // show all citations to ensure sources are visible (better than showing nothing)
    const groupCitations = group.modelsRepresented.length > 0
      ? allCitations.filter(citation =>
          citation.modelKey && group.modelsRepresented.includes(citation.modelKey)
        )
      : allCitations; // Fallback: show all citations if no models represented

    logger.info('[Citations] getCitationsForGroup called', {
      groupId: group.id,
      groupMemberCount: group.members.length,
      groupCitationsCount: groupCitations.length,
      allCitationsCount: allCitations.length,
      citationsMapSize: citationsByResponse.size,
      modelsRepresented: group.modelsRepresented,
      usingFallback: group.modelsRepresented.length === 0,
      note: group.modelsRepresented.length > 0
        ? 'Filtered citations to only models in group.modelsRepresented'
        : 'FALLBACK: Showing all citations (modelsRepresented is empty)'
    });

    return { groupCitations, allCitations };
  };

  const handleModelBadgeClick = (modelKey: string) => {
    // ✅ Enhanced logging - entry point
    logger.info('[ClaimGroups] Navigating to response detail for variation click', {
      modelKey,
      comparisonId,
      availableResponses: responses.length,
      timestamp: new Date().toISOString()
    });

    // ✅ Find the matching response using standardized modelKey field
    const response = responses.find((r: ModelResponse) => {
      // Use the modelKey field if available (new standardized approach)
      if ((r as any).modelKey) {
        const matches = (r as any).modelKey === modelKey;
        logger.debug('[ClaimGroups] Checking response match (using modelKey field)', {
          responseModelKey: (r as any).modelKey,
          targetModelKey: modelKey,
          matches,
          responseId: r.id
        });
        return matches;
      }

      // Fallback to manual construction for backward compatibility
      const responseModelKey = `${r.provider?.name || r.providerName || r.providerId}:${r.model?.name || r.modelName || r.modelId}`;
      const matches = responseModelKey === modelKey;
      logger.debug('[ClaimGroups] Checking response match (fallback construction)', {
        responseModelKey,
        targetModelKey: modelKey,
        matches,
        responseId: r.id
      });
      return matches;
    });

    if (!response) {
      // ✅ Enhanced error logging
      logger.error('[ClaimGroups] Response not found for model', {
        modelKey,
        comparisonId,
        availableModelKeys: responses.map(r => (r as any).modelKey || 'no-modelKey-field'),
        fallbackKeys: responses.map(r =>
          `${r.provider?.name || r.providerName || r.providerId}:${r.model?.name || r.modelName || r.modelId}`
        ),
        searchedFor: modelKey
      });
      alert(`Unable to find response for ${modelKey}. Please try again.`);
      return;
    }

    // ✅ Log successful match and navigation
    logger.info('[ClaimGroups] Navigating to Response Detail View', {
      modelKey,
      responseId: response.id,
      comparisonId,
      navigationType: 'hash-based'
    });

    // Store return state in sessionStorage for back button handling
    sessionStorage.setItem('consensusReturnPath', `/results/${comparisonId}`);
    sessionStorage.setItem('consensusReturnTab', 'consensus');

    // Navigate to results page with hash to open response detail
    // This matches the pattern used in ResultsNewRoot.handleOpenResponseDetail
    navigate(`/results/${comparisonId}#view=responses&id=${response.id}&from=consensus`);
  };

  if (loading) {
    return (
      <div className="bg-slate-800/30 rounded-lg p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mb-4" />
        <p className="text-slate-400 text-sm">Loading claim groups...</p>
      </div>
    );
  }

  // Show extraction progress only during manual re-extraction
  if (extractingCitations) {
    return (
      <div className="bg-slate-800/30 rounded-lg p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mb-4" />
        <p className="text-slate-400 text-sm">
          Re-extracting citations... ({extractionProgress.current}/{extractionProgress.total})
        </p>
        {extractionProgress.total > 0 && (
          <div className="mt-4 w-full max-w-xs mx-auto">
            <div className="bg-slate-700/30 rounded-full h-2 overflow-hidden">
              <div
                className="bg-cyan-500 h-full transition-all duration-300"
                style={{ width: `${(extractionProgress.current / extractionProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
        <p className="text-red-400 text-sm">{error || 'Failed to load claim groups'}</p>
      </div>
    );
  }

  const { summary, groups } = data;

  // Collect all unique models from groups
  const allModels = Array.from(
    new Set(groups.flatMap(g => g.modelsRepresented))
  ).sort();

  // Apply model filter
  const filteredGroups = selectedModel
    ? groups.filter(g => g.modelsRepresented.includes(selectedModel))
    : groups;

  // Calculate filtered summary counts
  const filteredSummary = {
    totalGroups: filteredGroups.length,
    universal: filteredGroups.filter(g => g.groupType === 'universal').length,
    majority: filteredGroups.filter(g => g.groupType === 'majority').length,
    disputed: filteredGroups.filter(g => g.groupType === 'disputed').length,
    unique: filteredGroups.filter(g => g.groupType === 'unique').length
  };

  // Pagination calculations
  const totalGroups = filteredGroups.length;
  const totalPages = Math.ceil(totalGroups / ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalGroups);
  const paginatedGroups = filteredGroups.slice(startIndex, endIndex);
  const showPagination = totalGroups > ITEMS_PER_PAGE;

  // Use filtered summary if model filter is active, otherwise use original summary (full counts)
  // This ensures category cards always show full counts, not filtered counts
  const displaySummary = selectedModel ? filteredSummary : (originalSummary || summary);

  // Mobile filter button component
  const MobileFilterButton = () => {
    const selectedModelDisplayName = selectedModel
      ? getModelDisplayInfo(selectedModel).displayName
      : null;

    return (
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg">
        <button
          onClick={() => setFilterSheetOpen(true)}
          className="w-full px-3 py-2 flex items-center justify-between text-left"
        >
          <span className="text-sm text-slate-300">
            {selectedModelDisplayName ? (
              <>
                Filtered: <span className="text-cyan-400">{selectedModelDisplayName}</span>
              </>
            ) : (
              'Filter by Model'
            )}
          </span>
          <span className="text-slate-400">{selectedModel ? '✕' : '▼'}</span>
        </button>
        {selectedModel && (
          <button
            onClick={() => setSelectedModel(null)}
            className="w-full px-3 py-2 text-xs text-cyan-400 hover:text-cyan-300 border-t border-slate-700/50"
          >
            Clear Filter
          </button>
        )}
      </div>
    );
  };

  // Mobile summary bar (horizontal)
  const MobileSummaryBar = () => (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {Object.entries(GROUP_TYPE_CONFIG).map(([type, config]) => {
        const count = displaySummary[type as keyof typeof displaySummary];
        const isActive = filter === type;

        return (
          <button
            key={type}
            onClick={() => setFilter(isActive ? null : type)}
            className={`
              flex-1 min-w-[80px] p-2 rounded-lg border transition-all
              ${isActive
                ? `bg-${config.color}-500/20 border-${config.color}-500/50`
                : 'bg-slate-800/30 border-slate-700/50'
              }
            `}
          >
            <div className="flex flex-col items-center gap-1">
              <span className="text-lg">{config.icon}</span>
              <span className="text-xl font-bold text-slate-200">{count}</span>
              <span className="text-[10px] text-slate-400 text-center leading-tight">
                {type === 'universal' ? 'All' : type === 'majority' ? 'Most' : type === 'disputed' ? 'Differ' : 'Unique'}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );

  // Desktop summary cards (unchanged)
  const DesktopSummaryCards = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {Object.entries(GROUP_TYPE_CONFIG).map(([type, config]) => {
        const count = displaySummary[type as keyof typeof displaySummary];
        const isActive = filter === type;

        return (
          <button
            key={type}
            onClick={() => setFilter(isActive ? null : type)}
            className={`
              p-3 rounded-lg border transition-all text-left
              ${isActive
                ? `bg-${config.color}-500/20 border-${config.color}-500/50`
                : 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600'
              }
            `}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{config.icon}</span>
              <span className="text-slate-300 text-sm font-medium">{config.label}</span>
            </div>
            <div className="text-2xl font-bold text-slate-200">{count}</div>
            <div className="text-xs text-slate-400">{config.description}</div>
          </button>
        );
      })}
    </div>
  );

  // Desktop model filter (unchanged)
  const DesktopModelFilter = () => (
    <>
      {allModels.length > 1 && (
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-3">
          <div className="text-xs text-slate-400 mb-2 uppercase tracking-wide">Filter by Model</div>
          <div className="flex flex-wrap items-center gap-2">
            {allModels.map(modelKey => {
              const { provider, displayName } = getModelDisplayInfo(modelKey);
              const isSelected = selectedModel === modelKey;
              const { model } = parseModelKey(modelKey);

              return (
                <button
                  key={modelKey}
                  type="button"
                  onClick={() => setSelectedModel(isSelected ? null : modelKey)}
                  aria-pressed={isSelected}
                  className={`relative rounded-xl transition-transform focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900 ${
                    isSelected
                      ? 'ring-2 ring-cyan-400 ring-offset-2 ring-offset-slate-900'
                      : 'hover:-translate-y-[1px]'
                  }`}
                  title={`Click to filter by ${displayName}`}
                >
                  <ModelBadge
                    provider={provider}
                    model={model}
                    displayName={displayName}
                    size="small"
                  />
                  {isSelected && (
                    <span className="absolute -top-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-cyan-500 text-[10px] font-bold text-slate-900 shadow-lg">
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
            {selectedModel && (
              <button
                type="button"
                onClick={() => setSelectedModel(null)}
                className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );

  // Missing Citations Warning Banner Component
  const MissingCitationsWarning = () => (
    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <span className="text-2xl flex-shrink-0">⚠️</span>
        <div className="flex-1">
          <h3 className="text-yellow-300 font-semibold mb-1">Citations Not Extracted</h3>
          <p className="text-yellow-200/80 text-sm mb-3">
            Your responses appear to contain sources and URLs, but citations haven't been extracted yet.
            This may happen if the extraction process was interrupted or failed.
          </p>
          <button
            onClick={handleRetriggerCitationExtraction}
            disabled={extractingCitations}
            className="px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 disabled:bg-yellow-500/10 border border-yellow-500/50 rounded-lg text-yellow-200 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {extractingCitations ? (
              <>
                <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                <span>Extracting... ({extractionProgress.current}/{extractionProgress.total})</span>
              </>
            ) : (
              <>
                <span>🔄</span>
                <span>Extract Citations Now</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Render mobile or desktop layout based on screen size */}
      {isMobile ? (
        <>
          {/* Mobile Horizontal Summary Bar */}
          <MobileSummaryBar />

          {/* Mobile Filter Button */}
          {allModels.length > 1 && <MobileFilterButton />}
        </>
      ) : (
        <>
          {/* Desktop Summary Cards */}
          <DesktopSummaryCards />

          {/* Desktop Model Filter */}
          <DesktopModelFilter />
        </>
      )}

      {/* Missing Citations Warning Banner */}
      {showMissingCitationsWarning && !extractingCitations && (
        <MissingCitationsWarning />
      )}

      {/* Claim Groups - Mobile (swipeable) or Desktop (paginated list) */}
      {isMobile ? (
        filteredGroups.length === 0 ? (
          <div className="bg-slate-800/30 rounded-lg p-8 text-center">
            <p className="text-slate-400 text-sm">
              No {filter ? GROUP_TYPE_CONFIG[filter as keyof typeof GROUP_TYPE_CONFIG].label.toLowerCase() : 'claim groups'} found.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredGroups.map(group => {
              const config = GROUP_TYPE_CONFIG[group.groupType];
              const isExpanded = expandedGroups.has(group.groupId);
              const { groupCitations, allCitations } = getCitationsForGroup(group);

              return (
                <div
                  key={group.groupId}
                  className={`bg-${config.color}-500/5 border border-${config.color}-500/30 rounded-lg p-4`}
                >
                  {/* Group Header */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-base flex-shrink-0">{config.icon}</span>
                      <span className="text-xs text-slate-400 uppercase tracking-wide">
                        {config.label}
                      </span>
                      {group.avgSimilarityScore && (
                        <span className="text-xs text-slate-500">
                          {(group.avgSimilarityScore * 100).toFixed(0)}% average similarity
                        </span>
                      )}
                    </div>

                    {/* Debug badges - Citations state */}
                    {import.meta.env.DEV && (
                      <div className="flex flex-wrap gap-2 mb-2 p-2 bg-slate-800/50 rounded border border-cyan-500/30">
                        <span className="text-xs px-2 py-1 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                          Group Citations: {groupCitations.length}
                        </span>
                        <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                          All Citations: {allCitations.length}
                        </span>
                        <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                          Map Size: {citationsByResponse.size}
                        </span>
                        <span className="text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                          Group Members: {group.memberCount}
                        </span>
                      </div>
                    )}

                    <ClaimCard
                      claimText={group.representativeClaimText}
                      claimType={group.members[0]?.claimType || 'fact'}
                      citations={groupCitations}
                      allCitations={allCitations}
                      extractedBy={`${group.memberCount} ${group.memberCount === 1 ? 'model' : 'models'}`}
                      showCitations={false}
                    />

                    {/* Source Pool - Deduplicated citations at group level */}
                    <SourcePoolDisplay
                      {...deduplicateAndCategorizeCitations(groupCitations)}
                    />
                  </div>

                  {/* Model Badges */}
                  <div className="mb-3">
                    <p className="text-xs text-slate-400 mb-2">
                      {group.modelsRepresented.length > 1 ? 'Models in consensus:' : 'Identified by:'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {group.modelsRepresented.map(modelKey => {
                      const { provider, displayName } = getModelDisplayInfo(modelKey);
                      return (
                        <button
                          key={modelKey}
                          onClick={() => handleModelBadgeClick(modelKey)}
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                          title={`Click to view full response from ${displayName}`}
                        >
                          <ModelBadge
                            provider={provider}
                            model={displayName}
                            size="small"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>

                  {/* Actions */}
                  <div className="flex gap-2 flex-wrap">
                    {group.memberCount > 1 && (
                      <button
                        onClick={() => toggleExpanded(group.groupId)}
                        className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors flex items-center gap-1.5"
                      >
                        <span>{isExpanded ? 'Hide' : 'See'} {group.memberCount} variations</span>
                        {isExpanded ? (
                          <ChevronUp className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5" />
                        )}
                      </button>
                    )}

                    <div className="flex gap-1 items-center">
                      <button
                        onClick={() => {
                          if (group.hasExplanation) {
                            toggleExplanation(group.groupId);
                          } else {
                            requestExplanation(group.groupId);
                          }
                        }}
                        disabled={loadingExplanation === group.groupId}
                        className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-slate-300 text-xs rounded transition-colors flex items-center gap-1.5"
                      >
                        <span>
                          {loadingExplanation === group.groupId
                            ? 'Loading...'
                            : group.hasExplanation
                            ? visibleExplanations.has(group.groupId)
                            ? '💭 Hide Why'
                            : '💭 See why'
                            : '💭 Explain why'
                          }
                        </span>
                        {group.hasExplanation && loadingExplanation !== group.groupId && (
                          visibleExplanations.has(group.groupId) ? (
                            <ChevronUp className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5" />
                          )
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Explanation */}
                  {group.explanation && visibleExplanations.has(group.groupId) && (
                    <div className="mt-3 bg-slate-900/30 rounded p-3 border border-slate-700/50">
                      <div className="text-xs text-slate-400 mb-1">💭 Explanation:</div>
                      <p className="text-slate-300 text-sm italic">{group.explanation}</p>
                    </div>
                  )}

                  {/* Expanded Variations */}
                  {isExpanded && (
                    <div className="mt-3 space-y-3 border-t border-slate-700/50 pt-3">
                      {group.members.map((member, idx) => {
                        const { provider, displayName } = getModelDisplayInfo(member.modelKey);
                        // Find response using standardized modelKey field
                        const response = responses.find(r => {
                          if ((r as any).modelKey) {
                            return (r as any).modelKey === member.modelKey;
                          }
                          // Fallback for backward compatibility
                          const responseModelKey = `${r.provider?.name || r.providerName || r.providerId}:${r.model?.name || r.modelName || r.modelId}`;
                          return responseModelKey === member.modelKey;
                        });
                        const memberCitations = response ? (citationsByResponse.get(response.id) || []) : [];

                        return (
                          <div
                            key={idx}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleModelBadgeClick(member.modelKey)}
                            onKeyDown={event => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleModelBadgeClick(member.modelKey);
                              }
                            }}
                            className="w-full bg-slate-900/50 border border-slate-700/30 rounded-lg p-3 hover:border-slate-600/50 hover:bg-slate-900/70 transition-all cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                            title={`Click to view full response from ${displayName}`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <ModelBadge
                                provider={provider}
                                model={displayName}
                                size="small"
                              />
                              <span className="text-xs text-slate-500">
                                {member.similarityToRepresentative === 1 ? 'Reference claim' : `${(member.similarityToRepresentative * 100).toFixed(0)}% match`}
                              </span>
                            </div>
                            <ClaimCard
                              claimText={member.claimText}
                              claimType={member.claimType}
                              citations={memberCitations}
                              allCitations={allCitations}
                              extractedBy={displayName}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : (
        // Desktop: Paginated list (unchanged behavior)
        <div className="space-y-3">
          {paginatedGroups.length === 0 ? (
            <div className="bg-slate-800/30 rounded-lg p-8 text-center">
              <p className="text-slate-400 text-sm">
                No {filter ? GROUP_TYPE_CONFIG[filter as keyof typeof GROUP_TYPE_CONFIG].label.toLowerCase() : 'claim groups'} found.
              </p>
            </div>
          ) : (
            paginatedGroups.map(group => {
              const config = GROUP_TYPE_CONFIG[group.groupType];
              const isExpanded = expandedGroups.has(group.groupId);
              const { groupCitations, allCitations } = getCitationsForGroup(group);

              return (
                <div
                  key={group.groupId}
                  className={`bg-${config.color}-500/5 border border-${config.color}-500/30 rounded-lg p-4`}
                >
                  {/* Group Header */}
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-base flex-shrink-0">{config.icon}</span>
                      <span className="text-xs text-slate-400 uppercase tracking-wide">
                        {config.label}
                      </span>
                      {group.avgSimilarityScore && (
                        <span className="text-xs text-slate-500">
                          {(group.avgSimilarityScore * 100).toFixed(0)}% average similarity
                        </span>
                      )}
                    </div>

                    <ClaimCard
                      claimText={group.representativeClaimText}
                      claimType={group.members[0]?.claimType || 'fact'}
                      citations={groupCitations}
                      allCitations={allCitations}
                      extractedBy={`${group.memberCount} ${group.memberCount === 1 ? 'model' : 'models'}`}
                      showCitations={false}
                    />

                    {/* Source Pool - Deduplicated citations at group level */}
                    <SourcePoolDisplay
                      {...deduplicateAndCategorizeCitations(groupCitations)}
                    />
                  </div>

                  {/* Model Badges */}
                  <div className="mb-3">
                    <p className="text-xs text-slate-400 mb-2">
                      {group.modelsRepresented.length > 1 ? 'Models in consensus:' : 'Identified by:'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {group.modelsRepresented.map(modelKey => {
                      const { provider, displayName } = getModelDisplayInfo(modelKey);
                      return (
                        <button
                          key={modelKey}
                          onClick={() => handleModelBadgeClick(modelKey)}
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                          title={`Click to view full response from ${displayName}`}
                        >
                          <ModelBadge
                            provider={provider}
                            model={displayName}
                            size="small"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    {group.memberCount > 1 && (
                      <div className="order-2 sm:order-1">
                        <button
                          onClick={() => toggleExpanded(group.groupId)}
                          className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors flex items-center gap-1.5"
                        >
                          <span>{isExpanded ? 'Hide' : 'Show'} {group.memberCount} variations</span>
                          {isExpanded ? (
                            <ChevronUp className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    )}

                    <div className="flex gap-1 items-center order-1 sm:order-2">
                      <button
                        onClick={() => {
                          if (group.hasExplanation) {
                            toggleExplanation(group.groupId);
                          } else {
                            requestExplanation(group.groupId);
                          }
                        }}
                        disabled={loadingExplanation === group.groupId}
                        className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-slate-300 text-xs rounded transition-colors flex items-center gap-1.5"
                      >
                        <span>
                          {loadingExplanation === group.groupId
                            ? 'Loading...'
                            : group.hasExplanation
                            ? visibleExplanations.has(group.groupId)
                              ? '💭 Hide explanation'
                              : '💭 View explanation'
                            : '💭 Explain why'
                          }
                        </span>
                        {group.hasExplanation && loadingExplanation !== group.groupId && (
                          visibleExplanations.has(group.groupId) ? (
                            <ChevronUp className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5" />
                          )
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Explanation */}
                  {group.explanation && visibleExplanations.has(group.groupId) && (
                    <div className="mt-3 bg-slate-900/30 rounded p-3 border border-slate-700/50">
                      <div className="text-xs text-slate-400 mb-1">💭 Explanation:</div>
                      <p className="text-slate-300 text-sm italic">{group.explanation}</p>
                    </div>
                  )}

                  {/* Expanded Variations */}
                  {isExpanded && (
                    <div className="mt-3 space-y-3 border-t border-slate-700/50 pt-3">
                      {group.members.map((member, idx) => {
                        const { provider, displayName } = getModelDisplayInfo(member.modelKey);
                        // Find response using standardized modelKey field
                        const response = responses.find(r => {
                          if ((r as any).modelKey) {
                            return (r as any).modelKey === member.modelKey;
                          }
                          // Fallback for backward compatibility
                          const responseModelKey = `${r.provider?.name || r.providerName || r.providerId}:${r.model?.name || r.modelName || r.modelId}`;
                          return responseModelKey === member.modelKey;
                        });
                        const memberCitations = response ? (citationsByResponse.get(response.id) || []) : [];

                        return (
                          <div
                            key={idx}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleModelBadgeClick(member.modelKey)}
                            onKeyDown={event => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleModelBadgeClick(member.modelKey);
                              }
                            }}
                            className="w-full bg-slate-900/50 border border-slate-700/30 rounded-lg p-3 hover:border-slate-600/50 hover:bg-slate-900/70 transition-all cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                            title={`Click to view full response from ${displayName}`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <ModelBadge
                                provider={provider}
                                model={displayName}
                                size="small"
                              />
                              <span className="text-xs text-slate-500">
                                {member.similarityToRepresentative === 1 ? 'Reference claim' : `${(member.similarityToRepresentative * 100).toFixed(0)}% match`}
                              </span>
                            </div>
                            <ClaimCard
                              claimText={member.claimText}
                              claimType={member.claimType}
                              citations={memberCitations}
                              allCitations={allCitations}
                              extractedBy={displayName}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Desktop Pagination Controls */}
      {!isMobile && showPagination && (
        <div className="flex items-center justify-between bg-slate-800/30 rounded-lg p-3 border border-slate-700/50">
          <div className="text-sm text-slate-400">
            Showing {startIndex + 1}-{endIndex} of {totalGroups}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-300 text-sm rounded transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage === totalPages - 1}
              className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-300 text-sm rounded transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Mobile Model Filter Bottom Sheet */}
      {isMobile && filterSheetOpen && portalTarget && createPortal(
        (
          <div
            className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm"
            onClick={() => setFilterSheetOpen(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute bottom-0 left-0 right-0 bg-slate-800 border-t-4 border-cyan-500 rounded-t-2xl max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-700">
                <h3 className="text-slate-200 font-medium">Filter by Model</h3>
                <button
                  onClick={() => setFilterSheetOpen(false)}
                  className="text-slate-400 hover:text-slate-200 text-2xl leading-none"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              {/* Model Grid */}
              <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
                <div className="grid grid-cols-2 gap-3">
                  {allModels.map(modelKey => {
                    const { provider, displayName } = getModelDisplayInfo(modelKey);
                    const isSelected = selectedModel === modelKey;
                    const { model } = parseModelKey(modelKey);
                    return (
                      <button
                        key={modelKey}
                        onClick={() => {
                          setSelectedModel(isSelected ? null : modelKey);
                          setFilterSheetOpen(false);
                        }}
                        className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition-all text-left ${
                          isSelected
                            ? 'bg-cyan-500/20 border-cyan-500/50 ring-2 ring-cyan-400'
                            : 'bg-slate-700/30 border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        {renderModelOptionContent(provider, model, displayName)}
                      </button>
                    );
                  })}
                </div>

                {/* Clear button */}
                {selectedModel && (
                  <button
                    onClick={() => {
                      setSelectedModel(null);
                      setFilterSheetOpen(false);
                    }}
                    className="w-full mt-4 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        ),
        portalTarget
      )}

      {/* Consensus Guide Modal */}
      <ConsensusGuideModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
      />

      {/* AI-generated disclaimer at bottom */}
      <div className="mt-6 text-[11px] text-slate-500">
        AI-generated · May contain errors.
      </div>
    </div>
  );
};
